/**
 * TCPN PHASE 10-B2B TRUST1 — VerifiedProvenanceTooltip
 * ====================================================
 * Reusable tooltip wrapper that surfaces the canonical TreatX verification
 * explanation. Renders ONLY when the wrapped badge represents a truly verified
 * provider — non-verified surfaces simply pass through the children unchanged
 * (no tooltip, no override).
 *
 * Trust contract (do not loosen):
 *   - copy comes exclusively from `lib/verificationCopy.js`
 *   - non-verified providers: NO tooltip is mounted
 *   - tooltip has zero animation, zero ranking impact, zero engagement metrics
 *   - works with hover and keyboard focus (Radix default)
 */
import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { VERIFICATION_COPY, isVerifiedProvider } from '../lib/verificationCopy';

export const VerifiedProvenanceTooltip = ({
  verificationStatus,
  children,
  side = 'top',
  testId = 'verified-provenance-tooltip',
}) => {
  // Pass-through when the provider isn't verified — non-verified surfaces must
  // never gain explanatory copy that would imply pending = "almost verified".
  if (!isVerifiedProvider(verificationStatus)) {
    return children;
  }

  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild data-testid={`${testId}-trigger`}>
          {children}
        </TooltipTrigger>
        <TooltipContent
          side={side}
          align="start"
          className="max-w-xs bg-white border border-slate-200 text-slate-700 shadow-md p-3 rounded-md"
          data-testid={testId}
        >
          <p className="text-xs font-semibold text-slate-900 mb-1">
            {VERIFICATION_COPY.title}
          </p>
          <ul className="space-y-1 text-[11px] leading-relaxed text-slate-600 list-none">
            {VERIFICATION_COPY.body.map((line, i) => (
              <li key={i} data-testid={`${testId}-line-${i}`}>{line}</li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default VerifiedProvenanceTooltip;
